/**
 * The API encodes narration as paragraphs joined by a blank line. Turning that back into a
 * list is the whole of the story feed's "render prose as real `<p>` elements", and it lives beside
 * `Prose.tsx` rather than inside it so it can be unit-tested (and so Fast Refresh keeps
 * working — a component module that exports a function is a full reload on every edit).
 *
 * **The mention spans are why this file grew a second function** (U6). `chapter.mentions`
 * carries offsets into the *raw* `narration`, and the split below collapses soft wraps and
 * trims — so a server offset maps to no index at all in the strings `toParagraphs` returns.
 * Patching offsets through the collapse is the obvious fix and it is the wrong one: every
 * consumer downstream would then be doing arithmetic against a string it did not build.
 *
 * `toParagraphRuns` cuts the raw narration at the span boundaries *first* and paragraph-splits
 * the run list afterwards, so each run carries its mention **by reference** and no offset
 * survives into rendering. `toParagraphs` is the same function with the runs flattened, which
 * is what keeps the two from ever disagreeing about where a paragraph ends.
 */
import type { Mention } from '@/api/generated';

/** One stretch of one paragraph. `mention` non-null means these words are a link. */
export interface Run {
  text: string;
  mention: Mention | null;
}

/**
 * A blank line — the paragraph separator, tolerating `\r\n` and runs of more than two
 * newlines. A single `\n` inside a paragraph is a soft wrap in the source and is collapsed
 * by `collapse` below, because that is what the DM means by it.
 */
const PARAGRAPH_BREAK = /\r?\n\s*\r?\n/;

const collapse = (text: string): string => text.replace(/\s*\r?\n\s*/g, ' ');

/**
 * Narration cut at the mention boundaries and then split into paragraphs.
 *
 * Spans that overlap, run backwards, or point outside the string are dropped rather than
 * clamped. The server already guarantees none of that (`quest/dm/mentions.py` records the
 * offsets against the text as it builds it), so this is the belt to that braces: a bad span
 * costs a link and never a mangled paragraph.
 */
export function toParagraphRuns(text: string, mentions: readonly Mention[] = []): Run[][] {
  const paragraphs: Run[][] = [];
  let current: Run[] = [];

  const endParagraph = (): void => {
    const finished = trimEdges(current.map((run) => ({ ...run, text: collapse(run.text) })));
    if (finished.length > 0) paragraphs.push(finished);
    current = [];
  };

  for (const segment of toSegments(text, mentions)) {
    // A mention is atomic: the grammar forbids a newline inside the label, so a marked span
    // can never contain a paragraph break and must never be split by one.
    if (segment.mention !== null) {
      current.push(segment);
      continue;
    }
    const pieces = segment.text.split(PARAGRAPH_BREAK);
    pieces.forEach((piece, index) => {
      if (index > 0) endParagraph();
      if (piece.length > 0) current.push({ text: piece, mention: null });
    });
  }
  endParagraph();

  return paragraphs;
}

/**
 * Split on blank lines. The mention-free path, and deliberately the *same* path — flattening
 * the runs rather than reimplementing the split is what makes "prose with no spans renders
 * exactly as it did before spans existed" true by construction rather than by inspection.
 */
export function toParagraphs(text: string): string[] {
  return toParagraphRuns(text).map((runs) => runs.map((run) => run.text).join(''));
}

/** The raw string cut at every span boundary, in order, with the gaps between them. */
function toSegments(text: string, mentions: readonly Mention[]): Run[] {
  const ordered = [...mentions].sort((a, b) => a.start - b.start);
  const segments: Run[] = [];
  let cursor = 0;

  for (const mention of ordered) {
    // `slice` counts UTF-16 code units, which is exactly what the server emits — the reason
    // `Mention.start` is documented in those units rather than in code points.
    if (mention.start < cursor || mention.end <= mention.start || mention.end > text.length) {
      continue;
    }
    if (mention.start > cursor) {
      segments.push({ text: text.slice(cursor, mention.start), mention: null });
    }
    segments.push({ text: text.slice(mention.start, mention.end), mention });
    cursor = mention.end;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), mention: null });

  return segments;
}

/**
 * Trim the paragraph's outer whitespace without trimming inside it, then drop whatever
 * collapsed to nothing. The trim crosses runs — a paragraph beginning with a marked name has
 * its leading space on a different run from the name itself.
 */
function trimEdges(runs: Run[]): Run[] {
  const trimmed = runs.map((run) => ({ ...run }));

  while (trimmed.length > 0) {
    const first = trimmed[0]!;
    first.text = first.text.replace(/^\s+/, '');
    if (first.text.length > 0) break;
    trimmed.shift();
  }
  while (trimmed.length > 0) {
    const last = trimmed[trimmed.length - 1]!;
    last.text = last.text.replace(/\s+$/, '');
    if (last.text.length > 0) break;
    trimmed.pop();
  }

  return trimmed.filter((run) => run.text.length > 0);
}
